import { useContext, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { Text, Box } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import RadioInput from 'components/fields/auto/RadioInput';

import UnlistedVehicles from 'views/auto_form/components/UnlistedVehicles';
import Divider from 'views/auto_form/components/Divider';
import Message from 'components/fields/auto/Message';

import Vehicle from './components/Vehicle';

import {
  FILE_TYPE,
  PROVINCE_ALBERTA,
  PROVINCE_NOVA_SCOTIA,
  PROVINCE_NEW_BRUNSWICK,
  isAtlanticProvince,
  isNonOntarioProvince,
} from 'variables/constants';
import { SECTIONS } from 'views/auto_form/constants';

import getValidationMessage from 'helpers/getValidationMessage';

import getRadioYesNo from 'helpers/getRadioYesNo';
import getVehicleSummary from 'views/auto_form/sections/Vehicles/helpers/getVehicleSummary';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import { changeVehicleSummary } from 'reducers/autoPersonalFormReducer';

const Vehicles = () => {
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const dispatch = useAppDispatch();

  const vehicles = useAppSelector(
    (state) => state.autoPersonalForm.data.vehicles,
  );

  const driversCount = useAppSelector(
    (state) => state.autoPersonalForm.data.drivers?.length || 0,
  );

  const vehicles_summary = useAppSelector(
    (state) => state.autoPersonalForm.data.vehicles_summary,
  );

  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isNonOntario = isNonOntarioProvince(province);
  const isAtlantic = isAtlanticProvince(province);
  const isAlberta = province === PROVINCE_ALBERTA;
  const isNovaScotia = province === PROVINCE_NOVA_SCOTIA;
  const isNewbrunswick = province === PROVINCE_NEW_BRUNSWICK;
  const isBaraEnabled = useFeatureFlag('enable-bara');

  const doesVehicleCountMatch = useMemo(() => {
    const totalVehicles = vehicles.length;
    const totalVehiclesSummary = vehicles_summary.total_vehicles;
    return totalVehicles === Number(totalVehiclesSummary);
  }, [vehicles.length, vehicles_summary.total_vehicles]);

  const validation021 = getValidationMessage(validations, '021');
  const validation086 = getValidationMessage(validations, '086');

  const onVehiclesSummaryChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    let payload = type === 'checkbox' ? checked : value;

    // Allow only whole numbers for total vehicles
    if (name === 'total_vehicles') {
      payload = value.replace(/\D/g, '');
    }

    dispatch(changeVehicleSummary({ name, value: payload }));
  };

  return (
    <Card mb="20px" id="sectionVehicles">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Described Automobile
      </Text>

      {vehicles.map((vehicle, index: number) => (
        <Vehicle key={index} vehicle={vehicle} index={index} />
      ))}

      {validation021 && <UnlistedVehicles />}

      <Divider>Summary</Divider>

      <Box mt="20px">
        <RadioInput
          section={SECTIONS.VEHICLES}
          label={
            isNonOntario
              ? 'Unless otherwise stated, the applicant is both the registered owner and the actual owner of the described automobile(s).'
              : 'Is the applicant both the Registered Owner and the Actual Owner of the described automobile(s)?'
          }
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(vehicles_summary.is_full_onwer)}
          onChange={(value) =>
            onVehiclesSummaryChange({
              target: { name: 'is_full_onwer', value: value === 'true' },
            })
          }
          message={{
            validationId: 'registered_vs_actual_owner',
            manual: {
              message:
                isAlberta || isNovaScotia
                  ? `Please refer to BIG Portfolio Underwriting${
                      isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''
                    }.`
                  : 'Please provide additional details',
              show: vehicles_summary.is_full_onwer === false,
            },
          }}
          required
        />

        {isNonOntario && vehicles_summary.is_full_onwer === false && (
          <>
            <Box mt="20px" w="50%">
              <FormInput
                section={SECTIONS.VEHICLES}
                label="Registered Owner Name"
                name="registered_owner_name"
                value={vehicles_summary.registered_owner_name || ''}
                onChange={onVehiclesSummaryChange}
                required
              />
            </Box>
            <Box mt="10px" w="50%">
              <FormInput
                section={SECTIONS.VEHICLES}
                label="Actual Owner Name"
                name="actual_owner_name"
                value={vehicles_summary.actual_owner_name || ''}
                onChange={onVehiclesSummaryChange}
                required
              />
            </Box>
          </>
        )}

        {isAlberta && (
          <Box mt="20px">
            <RadioInput
              section={SECTIONS.VEHICLES}
              label="Does the applicant owe any money to another insurer related to an Alberta Policy of auto insurance?"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(vehicles_summary.owes_money_to_insurer)}
              onChange={(value) =>
                onVehiclesSummaryChange({
                  target: {
                    name: 'owes_money_to_insurer',
                    value: value === 'true',
                  },
                })
              }
              required
            />
          </Box>
        )}

        <Box mt="20px">
          <RadioInput
            section={SECTIONS.VEHICLES}
            label={
              isAlberta
                ? 'Will the automobile be rented, leased, used for carrying passengers for compensation or hire (examples: taxi, transportation network automobile), carrying explosives or carrying radioactive material, even on an occasional basis?'
                : isAtlantic
                  ? 'Will the automobile be rented or leased, or used for carrying passengers for compensation or hire, or for carrying explosives or radioactive material?'
                  : 'Will any of the described automobiles be rented or leased to others, or used to carry passengers for compensation or hire, or haul a trailer, or carry explosives or radioactive material?'
            }
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(vehicles_summary.is_additional_risk)}
            onChange={(value) =>
              onVehiclesSummaryChange({
                target: { name: 'is_additional_risk', value: value === 'true' },
              })
            }
            message={{
              validationId: 'additional_risk',
              manual: {
                message: 'Please provide additional details',
                show: vehicles_summary.is_additional_risk,
              },
            }}
            required
          />
        </Box>

        <Box mt="20px" w="50%">
          <FormInput
            section={SECTIONS.VEHICLES}
            label={
              isNovaScotia
                ? 'Total number of private passenger vehicles in the household, including those already listed. (*Household – A family unit, resident in the same living quarters)'
                : isNonOntario
                  ? 'Total number of private passenger vehicles in the household, including those already listed.'
                  : 'Total number of automobiles in the household or business. '
            }
            name="total_vehicles"
            value={vehicles_summary.total_vehicles}
            onChange={onVehiclesSummaryChange}
            message={{
              validationId: 'total_vehicles',
              manual: {
                message:
                  'Please provide additional remarks on why the vehicles listed on the quote do not match the total number of vehicles in the household or business.',
                show: !doesVehicleCountMatch,
              },
            }}
            required
          />
        </Box>

        {isAtlantic && (
          <Box mt="20px" w="50%">
            <FormInput
              section={SECTIONS.VEHICLES}
              label={
                isNovaScotia
                  ? 'Total number of licensed drivers in the household, including those already listed (Household – A family unit, resident in the same living quarters)'
                  : 'Total number of licensed drivers in the household, including those already listed'
              }
              name="total_licensed_drivers"
              value={vehicles_summary.total_licensed_drivers || ''}
              onChange={onVehiclesSummaryChange}
              helpText={
                isNewbrunswick &&
                'List all drivers in the household not shown in the drivers section, please provide the name, drivers license number, and date of birth'
              }
              message={{
                validationId: 'total_licensed_drivers',
                manual: {
                  message:
                    'If more than the drivers listed, provide each additional driver’s name, driver’s licence number and date of birth in the Remarks section.',
                  show:
                    Number(vehicles_summary.total_licensed_drivers) >
                    driversCount,
                },
              }}
            />
          </Box>
        )}

        {isNonOntario && (
          <Box mt="20px" w="50%">
            <FormInput
              section={SECTIONS.VEHICLES}
              label={
                isNovaScotia
                  ? 'Number of non-licensed residents in the household (Household – A family unit, resident in the same living quarters)'
                  : 'Number of non-licensed residents in the household'
              }
              name="non_licensed_residents_count"
              value={vehicles_summary.non_licensed_residents_count || ''}
              onChange={onVehiclesSummaryChange}
              message={{
                validationId: 'non_licensed_residents',
                manual: {
                  message:
                    'Provide the name and date of birth of each non-licensed resident in the Remarks section.',
                  show:
                    Number(vehicles_summary.non_licensed_residents_count) > 0,
                },
              }}
            />
          </Box>
        )}

        {isAtlanticProvince(province) && (
          <Box mt="20px">
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Description of owned trailers not listed in the vehicle section"
              name="trailer_description"
              value={vehicles_summary.trailer_description || ''}
              onChange={onVehiclesSummaryChange}
            />
          </Box>
        )}
      </Box>

      {!isAlberta &&
        !isNewbrunswick &&
        !isNovaScotia &&
        vehicles.map((vehicle, index: number) => (
          <>
            <Divider mt="20px">
              Vehicle {isNonOntario ? 'registration' : 'ownership'} -{' '}
              {getVehicleSummary(vehicle, index)}
            </Divider>

            {isNonOntario ? (
              vehicle.is_leased ? (
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="vehicle_lease_agreement"
                  file={{
                    ...FILE_TYPE.TYPE_135,
                    mandatory: vehicle.is_purchased_new === true,
                  }}
                  manual={{
                    message: 'Please upload a copy of the Lease Agreement',
                    show: true,
                  }}
                  storageKey="vehicle"
                  index={index + 1}
                  hideRemarks
                  hideMessage
                />
              ) : (
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="vehicle_bill_of_sale"
                  file={{
                    ...FILE_TYPE.TYPE_040,
                    mandatory: vehicle.is_purchased_new === true,
                  }}
                  manual={{
                    message: 'Please upload a copy of the Bill of Sale',
                    show: true,
                  }}
                  storageKey="vehicle"
                  index={index + 1}
                  hideRemarks
                  hideMessage
                />
              )
            ) : (
              <Message
                section={SECTIONS.VEHICLES}
                validationId="vehicle_ownership"
                file={FILE_TYPE.TYPE_COPY_OF_VEHICLE_OWNERSHIP}
                manual={{
                  message: 'Please upload a copy of "vehicle ownership"',
                  show: true,
                }}
                storageKey="vehicle"
                index={index + 1}
                hideRemarks
                hideMessage
              />
            )}
          </>
        ))}

      {validation086 && (
        <>
          <Divider mt="20px">Increase in Pleasure Use Vehicles</Divider>

          <Message section={SECTIONS.VEHICLES} validationId="086" />
        </>
      )}
    </Card>
  );
};

export default Vehicles;
